import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface TourTooltipProps {
  style: CSSProperties
  title: string
  /** A plain string renders as a paragraph; an array renders as a bulleted list. */
  description: string | string[]
  step: number
  total: number
  isFirst: boolean
  isLast: boolean
  /** When true, the highlighted element itself must be clicked to proceed — Next is disabled. */
  isWaitingForAction: boolean
  /** Validation message shown when Next was clicked but the step's requirement wasn't met. */
  blockedMessage?: string | null
  onPrev: () => void
  onNext: () => void
  onSkip: () => void
  onFinish: () => void
}

const VIEWPORT_MARGIN = 16

function toPx(value: CSSProperties[keyof CSSProperties]): string | undefined {
  if (value === undefined) return undefined
  return typeof value === 'number' ? `${value}px` : String(value)
}

/** Renders `**bold**` markers in step descriptions (TourSteps.ts) as <strong> instead of literal asterisks. */
function renderWithBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-[var(--text)]">{part.slice(2, -2)}</strong>
    }
    return part
  })
}

export function TourTooltip({
  style,
  title,
  description,
  step,
  total,
  isFirst,
  isLast,
  isWaitingForAction,
  blockedMessage,
  onPrev,
  onNext,
  onSkip,
  onFinish,
}: TourTooltipProps) {
  const primaryBtnRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [finalStyle, setFinalStyle] = useState<CSSProperties>(style)

  useEffect(() => {
    primaryBtnRef.current?.focus()
  }, [step])

  // Position the tooltip using the caller's best guess, then measure its *actual* rendered
  // size and clamp it back within the viewport if it overflows — the caller can't know the
  // real height/width in advance (content length varies per step), so a fixed reservation
  // (e.g. "assume ~220px below the target") isn't reliable at 100% zoom on smaller viewports.
  useLayoutEffect(() => {
    const el = tooltipRef.current
    if (!el) {
      setFinalStyle(style)
      return
    }

    el.style.top = toPx(style.top) ?? ''
    el.style.left = toPx(style.left) ?? ''
    el.style.right = toPx(style.right) ?? ''
    el.style.bottom = toPx(style.bottom) ?? ''

    const r = el.getBoundingClientRect()
    const corrected: CSSProperties = { ...style }

    if (r.right > window.innerWidth - VIEWPORT_MARGIN) {
      corrected.left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - r.width)
      corrected.right = undefined
    }
    if (r.bottom > window.innerHeight - VIEWPORT_MARGIN) {
      corrected.top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - r.height)
      corrected.bottom = undefined
    }
    if (r.top < VIEWPORT_MARGIN) {
      // Clear `bottom` too — steps with placement="above" position via `bottom` only (no
      // `top`), so it can still be set here. Leaving both `top` and `bottom` on a fixed
      // element makes the browser stretch/shrink it to fill that gap instead of sizing to
      // its content, squashing the tooltip and hiding part of its content.
      corrected.top = VIEWPORT_MARGIN
      corrected.bottom = undefined
    }
    if (r.left < VIEWPORT_MARGIN) {
      corrected.left = VIEWPORT_MARGIN
      corrected.right = undefined
    }

    setFinalStyle(corrected)
    // Re-measure whenever the incoming position changes or the content changes size
  }, [style, title, description, blockedMessage, isWaitingForAction, isFirst, isLast])

  return (
    <div
      ref={tooltipRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-tooltip-title"
      aria-describedby="tour-tooltip-desc"
      className={cn(
        'fixed z-[902] w-[320px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)]',
        'flex flex-col bg-[var(--surface)] rounded-[var(--radius)] border border-[var(--border)]',
        'shadow-2xl tour-tooltip-fade'
      )}
      style={finalStyle}
    >
      {/* Scrollable content — can grow tall with long descriptions without pushing the
          footer buttons out of view */}
      <div className="p-5 pb-0 overflow-y-auto min-h-0 flex-1">
        <span className="inline-flex items-center px-2 py-[3px] rounded-full bg-[rgba(0,121,189,0.1)] text-[11px] font-bold text-[var(--accent)] tracking-[0.4px] mb-2">
          {getMessage('tour.tooltip.step_counter', { current: String(step), total: String(total) })}
        </span>

        <h2
          id="tour-tooltip-title"
          className="text-[15px] font-bold text-[var(--text)] mb-1.5"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {title}
        </h2>

        {Array.isArray(description) ? (
          <ul
            id="tour-tooltip-desc"
            className="text-[12.5px] text-[var(--text-dim)] leading-relaxed pb-4 list-disc pl-4 space-y-1"
          >
            {description.map((item, i) => (
              <li key={i}>{renderWithBold(item)}</li>
            ))}
          </ul>
        ) : (
          <p
            id="tour-tooltip-desc"
            className="text-[12.5px] text-[var(--text-dim)] leading-relaxed pb-4"
          >
            {renderWithBold(description)}
          </p>
        )}
      </div>

      {/* Footer — always fully visible, never scrolled out of view */}
      <div className="px-5 pb-5 pt-3 flex-shrink-0">
        {blockedMessage && (
          <p className="text-[11.5px] font-semibold text-[var(--error)] mb-3" role="alert">
            {blockedMessage}
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="text-[11.5px] font-semibold text-[var(--text-muted)] hover:text-[var(--error)] transition-colors duration-150"
          >
            {getMessage('tour.tooltip.button_skip')}
          </button>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={onPrev}
                className={cn(
                  'flex items-center gap-1 px-3 py-[7px] rounded-[var(--radius-sm)] border border-[var(--border)]',
                  'bg-[var(--surface2)] text-[var(--text-dim)] text-[12px] font-semibold',
                  'hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all duration-150'
                )}
              >
                <ChevronLeft size={13} strokeWidth={2.5} />
                {getMessage('tour.tooltip.button_previous')}
              </button>
            )}
            {isWaitingForAction ? (
              <span className="text-[11.5px] font-semibold text-[var(--accent)] italic">
                {getMessage('tour.tooltip.waiting_hint')}
              </span>
            ) : (
              <button
                ref={primaryBtnRef}
                type="button"
                onClick={isLast ? onFinish : onNext}
                className={cn(
                  'flex items-center gap-1 px-4 py-[7px] rounded-[var(--radius-sm)] text-white text-[12px] font-semibold',
                  'hover:opacity-90 transition-all duration-150 border border-transparent'
                )}
                style={{ background: 'linear-gradient(135deg, var(--accent), #2563eb)' }}
              >
                {isLast ? (
                  <>
                    <Check size={13} strokeWidth={2.5} />
                    {getMessage('tour.tooltip.button_finish')}
                  </>
                ) : (
                  <>
                    {getMessage('tour.tooltip.button_next')}
                    <ChevronRight size={13} strokeWidth={2.5} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
